import {
  BMButton,
  BMComponentsType,
  BMInputField,
  BMPrimitives,
  BMText,
  BMTray,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  Dimensions,
  FlatList,
  KeyboardAvoidingView,
  Pressable,
  StyleSheet,
} from "react-native";

import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { ErrorSection } from "../../Error";
import { useGetValue } from "../hooks";
import { BMDropdownMobileOptionsProps, BMDropdownOptionsValue } from "../types";

import IcSystemSearch from "@traveloka/icon-kit-web/react/IcSystemSearch";
import htmr from "htmr";

import { appendTestId } from "../../../utils";
import DotLoader from "../../DotLoader";
import { useScrollLock } from "../../hooks";
import { ModalManager } from "../../Modal";
import BMView from "../../View/BMView";
import BMDropdownNotFoundOptions from "./BMDropdownNotFoundOptions";
import BMDropdownOptionItem from "./BMDropdownOptionItem";

const CONTENT_MAX_HEIGHT = Dimensions.get("window").height * 0.32;
const ITEM_HEIGHT = 48;

export default function BMDropdownMobileOptions<T>({
  selected: initialSelected,
  show,
  option,
  isRadio,
  optionsSection,
  searchable,
  onClose,
  onChange,
  getOptionValue,
  testID,
}: BMDropdownMobileOptionsProps<T>) {
  const [query, setQuery] = useState("");
  const [isVisible, setIsVisible] = useState(show);
  const [selected, setSelected] = useState<BMDropdownOptionsValue<T> | null>(
    initialSelected
  );

  const { placeholder, noResultCaption, NoResultFooterComponent } =
    typeof searchable !== "boolean" && searchable
      ? searchable
      : {
          placeholder: undefined,
          noResultCaption: undefined,
          NoResultFooterComponent: null,
        };

  const { BMComponents } = useBMTheme();
  const { CorporateGeneralErrorMessage: cr } = useMobileContentResource();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const trayId = useId();
  const getValue = useGetValue(getOptionValue);
  useScrollLock(!ModalManager.empty);

  const filteredOptions = useMemo(
    () =>
      option.items.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase())
      ),
    [query, option.items]
  );

  useEffect(() => {
    setIsVisible(show);
    if (show) {
      setQuery("");
      setSelected(initialSelected);
      ModalManager.add(trayId, setIsVisible);
    } else {
      ModalManager.remove(trayId);
    }
  }, [show, initialSelected]);

  const handleOptionPress = useCallback(
    (item: BMDropdownOptionsValue<T>) => setSelected(item),
    []
  );

  const renderOptionItem = useCallback(
    ({ item, index }: { item: BMDropdownOptionsValue<T>; index: number }) => (
      <Pressable
        onPress={() => handleOptionPress(item)}
        testID={appendTestId(testID, `option-${index}`)}
      >
        <BMDropdownOptionItem
          isMobile
          label={item.label}
          query={query}
          options={item.options}
          isRadio={isRadio}
          isSelected={getValue(item.value) === getValue(selected?.value)}
        />
      </Pressable>
    ),
    [handleOptionPress, getValue, isRadio, query, selected]
  );

  function renderContent() {
    if (option.isLoading) {
      return (
        <BMView style={styles.marginVerticalLG}>
          <DotLoader />
        </BMView>
      );
    }

    if (option.isError) {
      return (
        <ErrorSection
          type="LARGE"
          title={cr.perSectionTitle}
          caption={cr.perSectionDescription}
          button={{
            type: "PRIMARY",
            label: cr.perSectionCTA,
            onPress: option.onClickRetry ?? (() => {}),
          }}
          style={styles.errorWrapperStyle}
        />
      );
    }

    if (filteredOptions.length > 0) {
      return (
        <FlatList
          data={filteredOptions}
          renderItem={renderOptionItem}
          showsVerticalScrollIndicator={false}
          style={{ maxHeight: CONTENT_MAX_HEIGHT }}
          ItemSeparatorComponent={() => <BMView style={styles.divider} />}
          getItemLayout={(_, index) => ({
            length: ITEM_HEIGHT,
            offset: ITEM_HEIGHT * index,
            index,
          })}
          keyExtractor={(_, index) => `dropdown-option-${index}`}
        />
      );
    }

    return (
      <BMDropdownNotFoundOptions
        isMobile
        query={query}
        noResultCaption={noResultCaption}
        NoResultFooterComponent={
          typeof NoResultFooterComponent === "function"
            ? NoResultFooterComponent
            : undefined
        }
      />
    );
  }

  function renderOptionsSection() {
    if (!optionsSection) return null;

    const optionDescription = optionsSection.description
      ? htmr(optionsSection.description, {
          transform: {
            b: (node) => (
              <BMText variant="uiSmallEm" ink="BMStyleTextInkSecondary">
                {node.children}
              </BMText>
            ),
          },
        })
      : null;

    return (
      <BMView spacing="md" style={styles.marginTopMD}>
        <BMText variant="titleNormal">{optionsSection.title}</BMText>
        <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
          {optionDescription}
        </BMText>
      </BMView>
    );
  }

  useEffect(() => {
    return () => ModalManager.remove(trayId);
  }, [trayId]);

  return (
    <BMTray
      fitContent
      isVisible={isVisible}
      onTrayClose={onClose}
      {...(option.isLoading || !option.isError
        ? {
            footerComponent: () => (
              <BMDropdownMobileOptionsFooter
                hasCancelButton
                onClose={onClose}
                onConfirm={() => onChange(selected)}
                testID={testID}
              />
            ),
          }
        : {})}
    >
      <BMView spacing="xl" style={optionsSection?.wrapperStyles}>
        {(optionsSection || searchable) && (
          <BMView spacing="md">
            {renderOptionsSection()}
            {!option.isLoading && !option.isError && searchable && (
              <KeyboardAvoidingView>
                <BMInputField
                  value={query}
                  LeadingIcon={IcSystemSearch}
                  placeholder={placeholder ? placeholder : "Search..."}
                  onChange={(e) => setQuery(e.nativeEvent.text)}
                />
              </KeyboardAvoidingView>
            )}
          </BMView>
        )}
        {renderContent()}
      </BMView>
    </BMTray>
  );
}

type BMDropdownMobileOptionsFooterProps = {
  hasCancelButton?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  testID?: string;
};

function BMDropdownMobileOptionsFooter({
  hasCancelButton = true,
  onClose,
  onConfirm,
  testID,
}: BMDropdownMobileOptionsFooterProps) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  const { CorporateCommon: cr } = useMobileContentResource();
  return (
    <BMView row spacing="lg" style={styles.footerWrapper}>
      {hasCancelButton && (
        <BMView flex>
          <BMButton
            role="SECONDARY"
            size="LARGE"
            label={cr.cancelText}
            onPress={onClose}
            stretchable
            testID={appendTestId(testID, "cancel")}
          />
        </BMView>
      )}
      <BMView flex>
        <BMButton
          role="PRIMARY"
          size="LARGE"
          label={cr.confirmText}
          onPress={onConfirm}
          stretchable
          testID={appendTestId(testID, "confirm")}
        />
      </BMView>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    marginTopMD: {
      marginTop: BMPrimitives.BMSize.md,
    },
    marginVerticalLG: {
      marginVertical: BMPrimitives.BMSize.lg,
    },
    marginBottomXL5: {
      marginBottom: BMPrimitives.BMSize.xl5,
    },
    footerWrapper: {
      margin: -BMPrimitives.BMSize.xl,
      padding: BMPrimitives.BMSize.xl,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorHighlight,
    },
    divider: {
      borderBottomWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleCardBordercolor,
    },
    errorWrapperStyle: {
      minWidth: "auto",
      marginHorizontal: -BMPrimitives.BMSize.md,
    },
    notFoundIllustration: {
      width: 48,
      height: 48,
    },
  });
}
